/**
 * RumahKira estimate engine
 *
 * One calculation pipeline for both estimate modes:
 * - Quick estimate: 4 questions; room counts and structure are filled in with typical defaults
 * - Detailed estimate: the user specifies structure, rooms, doors and windows
 *
 * Built-up area is always the TOTAL floor area of all storeys (as used in Malaysian listings).
 */

import { COST_RATES, HOUSE_TYPES, FINISHING_MATERIAL_MULTIPLIERS, getFloorCostFactor } from './costRates';
import { quantityRange } from './format';

const SQM_TO_SQFT = 10.764;
const M_TO_FT = 3.281;
const INCH_TO_M = 0.0254;

const DRY_VOLUME_FACTOR = 1.54; // wet concrete → dry material volume
const CONCRETE_WASTAGE = 1.05;
const CEMENT_DENSITY = 1440; // kg/m³
const CEMENT_BAG_KG = 50;
const ROOF_OVERHANG_FACTOR = 1.15; // roof plan area vs footprint
const ROOF_PITCH_FACTOR = 1.15; // sloped surface vs plan area for a ~30° pitched roof

// ---------------------------------------------------------------------------
// Inputs
// ---------------------------------------------------------------------------

/**
 * Typical specification for a house, used by the quick estimate and as the
 * starting values of the detailed form.
 */
export const getDefaultSpecs = (houseType = 'terrace', builtUpArea = 1200, floors = 1) => {
  const area = Number(builtUpArea) || 1200;
  const bedrooms = area < 900 ? 2 : area < 1600 ? 3 : area < 2600 ? 4 : 5;
  const bathrooms = bedrooms <= 2 ? 1 : bedrooms - 1;
  const kitchen = 1;
  const store = area < 900 ? 0 : 1;

  return {
    houseType,
    builtUpArea: area,
    floors: Number(floors) || 1,
    wallHeight: 10,
    wallType: 'brick45',
    foundationType: Number(floors) > 1 ? 'pad' : 'strip',
    roofType: 'metal',
    ceilingType: 'gypsum',
    bedrooms,
    bathrooms,
    kitchen,
    livingArea: 1,
    store,
    mainDoors: 2,
    roomDoors: bedrooms + kitchen + store,
    windows: bedrooms * 2 + 2,
  };
};

/**
 * Derive geometry used by all category calculations
 */
export const deriveCalculationParams = (formData) => {
  const spec = { ...getDefaultSpecs(formData.houseType, formData.builtUpArea, formData.floors), ...formData };

  const builtUpArea = parseFloat(spec.builtUpArea) || 1000;
  const floors = Math.min(Math.max(parseInt(spec.floors, 10) || 1, 1), 3);
  const wallHeight = parseFloat(spec.wallHeight) || 10;
  const count = (v) => Math.max(parseInt(v, 10) || 0, 0);

  const bedrooms = count(spec.bedrooms);
  const bathrooms = count(spec.bathrooms);
  const kitchen = count(spec.kitchen);
  const livingArea = count(spec.livingArea);
  const store = count(spec.store);
  const mainDoors = count(spec.mainDoors);
  const roomDoors = count(spec.roomDoors);
  const windows = count(spec.windows);

  // Areas: built-up area is the total of all floors
  const totalFloorArea = builtUpArea;
  const footprint = builtUpArea / floors; // ground floor area
  const roofArea = footprint * ROOF_OVERHANG_FACTOR;

  // Wall lengths (plan assumed roughly square)
  const perimeter = Math.sqrt(footprint) * 4;
  const externalWallLength = perimeter * floors;
  const internalWallLength = (bedrooms + bathrooms + kitchen + livingArea + store) * 20; // ~20 ft per room
  const combinedWallLength = externalWallLength + internalWallLength;
  const grossWallArea = combinedWallLength * wallHeight;

  // Openings (standard sizes, one door per bathroom)
  const totalOpeningsArea = mainDoors * 21 + roomDoors * 17.5 + bathrooms * 14 + windows * 12;
  const netWallArea = Math.max(grossWallArea - totalOpeningsArea, 0);

  const houseType = HOUSE_TYPES[spec.houseType] ? spec.houseType : 'terrace';
  const finishingLevel = COST_RATES[spec.finishingLevel] ? spec.finishingLevel : 'standard';
  const tileFactor = (FINISHING_MATERIAL_MULTIPLIERS[finishingLevel] || FINISHING_MATERIAL_MULTIPLIERS.standard).tiles;

  return {
    builtUpArea,
    totalFloorArea,
    footprint,
    floors,
    roofArea,
    perimeter,
    combinedWallLength,
    grossWallArea,
    netWallArea,
    totalOpeningsArea,
    wallHeight,
    wallType: spec.wallType,
    roofType: spec.roofType,
    ceilingType: spec.ceilingType,
    foundationType: spec.foundationType,
    houseType,
    finishingLevel,
    // Basic finishes tile only part of the floor (rest is cement render)
    floorTileCoverage: Math.min(tileFactor, 1),
    wallTileArea: (bathrooms * 80 + kitchen * 30) * tileFactor,
    bedrooms,
    bathrooms,
    kitchen,
    livingArea,
    store,
    mainDoors,
    roomDoors,
    windows,
  };
};

// ---------------------------------------------------------------------------
// Category calculations
// ---------------------------------------------------------------------------

/** Cement bags + sand/stone volumes for a wet concrete volume and mix (cement:sand:stone) */
const concreteMaterials = (wetVolume, [c, s, a]) => {
  const parts = c + s + a;
  const dry = wetVolume * DRY_VOLUME_FACTOR * CONCRETE_WASTAGE;
  return {
    cement: ((dry * c) / parts) * CEMENT_DENSITY / CEMENT_BAG_KG,
    sand: (dry * s) / parts,
    stone: (dry * a) / parts,
  };
};

const M15 = [1, 2, 4];
const M20 = [1, 1.5, 3];

const calculateFoundation = ({ perimeter, footprint, floors, foundationType }) => {
  if (foundationType === 'pad') {
    const columnCount = Math.ceil(footprint / 150); // 1 column per 150 sqft
    const padSize = (floors > 1 ? 36 : 30) * INCH_TO_M;
    const wetVolume = padSize * padSize * 12 * INCH_TO_M * columnCount;
    return { ...concreteMaterials(wetVolume, M20), steel: wetVolume * 80, volume: wetVolume, columnCount };
  }

  if (foundationType === 'raft') {
    const areaSqm = footprint / SQM_TO_SQFT;
    const wetVolume = areaSqm * 0.2; // 200mm raft
    return { ...concreteMaterials(wetVolume, M20), steel: areaSqm * 8, volume: wetVolume };
  }

  // Strip footing 18" × 9" under external walls + 30% for internal load-bearing walls
  const lengthM = (perimeter * 1.3) / M_TO_FT;
  const wetVolume = lengthM * 18 * INCH_TO_M * 9 * INCH_TO_M;
  return { ...concreteMaterials(wetVolume, M15), steel: lengthM * 3, volume: wetVolume };
};

const calculateStructure = ({ footprint, floors, perimeter }) => {
  const slabAreaSqm = footprint / SQM_TO_SQFT;
  const groundSlab = slabAreaSqm * 0.1; // 100mm ground slab
  const upperSlabs = slabAreaSqm * 0.125 * (floors - 1); // 125mm suspended slabs

  const columnCount = Math.ceil(footprint / 150);
  const columns = columnCount * floors * 0.23 * 0.23 * 3; // 9"×9", 3m per floor
  const beams = ((perimeter * 1.3 * floors) / M_TO_FT) * 0.23 * 0.3; // 9"×12"

  const volume = groundSlab + upperSlabs + columns + beams;

  // Steel: BRC mesh ~5 kg/m² on ground slab, ~9 kg/m² in suspended slabs,
  // columns ~100 kg/m³, beams ~120 kg/m³
  const steel = slabAreaSqm * 5 + slabAreaSqm * 9 * (floors - 1) + columns * 100 + beams * 120;

  return { ...concreteMaterials(volume, M20), steel, volume, columnCount };
};

const calculateWalls = ({ netWallArea, wallType }) => {
  const unitsPerSqft = wallType === 'brick9' ? 10 : wallType === 'block' ? 1.125 : 5;
  const units = netWallArea * unitsPerSqft;

  // Mortar per unit (bricks); blocks use ~4× the mortar of a brick
  const mortarFactor = wallType === 'block' ? 4 : 1;
  const layingCement = units * 0.00281 * mortarFactor;
  const layingSand = units * 0.0004 * mortarFactor;

  // Plaster both faces of all walls (openings are not plastered)
  const plasterArea = netWallArea * 2;
  const plasterCement = plasterArea * 0.01129;
  const plasterSand = plasterArea * 0.00169;

  // Paint both faces, 2 coats at ~100 sqft per litre; primer ~10% of paint
  const paint = (plasterArea * 2) / 100;

  return {
    units,
    isBlock: wallType === 'block',
    cement: layingCement + plasterCement,
    sand: layingSand + plasterSand,
    paint,
    primer: paint * 0.1,
  };
};

const calculateFlooring = ({ totalFloorArea, floorTileCoverage, wallTileArea }) => {
  const floorTiles = totalFloorArea * floorTileCoverage * 1.1; // 10% cutting wastage
  const wallTiles = wallTileArea * 1.1;
  const tiledArea = floorTiles + wallTiles;

  // 25mm cement-sand screed (1:3) under all floors
  const screedVolume = (totalFloorArea / SQM_TO_SQFT) * 0.025 * 1.1;

  return {
    floorTiles,
    wallTiles,
    adhesiveBags: (tiledArea * 0.4) / 25, // ~4 kg/m² (0.4 kg/sqft), 25kg bags
    grout: tiledArea * 0.05, // ~0.5 kg/m²
    screedCement: (screedVolume * 0.25 * CEMENT_DENSITY) / CEMENT_BAG_KG,
    screedSand: screedVolume * 0.75 * 1.2,
  };
};

const calculateRoofing = ({ roofArea, roofType }) => {
  if (roofType === 'concrete') {
    const areaSqm = roofArea / SQM_TO_SQFT;
    const wetVolume = areaSqm * 0.125; // 125mm flat slab
    return { type: 'concrete', ...concreteMaterials(wetVolume, M20), steel: areaSqm * 6, volume: wetVolume };
  }

  const surface = roofArea * ROOF_PITCH_FACTOR;
  if (roofType === 'tiles') {
    // Concrete/clay roof tiles: ~10.8 tiles per m² (≈1 per sqft), 10% wastage
    return { type: 'tiles', tiles: surface * 1.0 * 1.1 };
  }

  return { type: 'metal', sheets: surface * 1.15, screws: surface * 0.5 }; // 15% side/end laps
};

const calculateCeiling = ({ totalFloorArea, ceilingType }) => {
  if (ceilingType === 'pvc') {
    return { type: 'pvc', panels: totalFloorArea * 1.1 };
  }
  if (ceilingType === 'plaster') {
    return { type: 'plaster', cement: totalFloorArea * 0.01129, sand: totalFloorArea * 0.00169 };
  }
  return {
    type: 'gypsum',
    boards: (totalFloorArea * 1.1) / 32, // 4' × 8' boards
    furring: totalFloorArea / 4,
  };
};

const calculateServices = ({ totalFloorArea, bathrooms, bedrooms, kitchen, livingArea }) => {
  const rooms = bathrooms + bedrooms + kitchen + livingArea;
  return {
    lightPoints: totalFloorArea / 50,
    socketPoints: rooms * 3,
    fanPoints: bedrooms + livingArea,
    waterPoints: bathrooms * 4 + kitchen * 2,
  };
};

// ---------------------------------------------------------------------------
// Costs
// ---------------------------------------------------------------------------

export const COST_CATEGORIES = [
  { id: 'foundation', percentage: 12, label: 'Foundation', labelMy: 'Asas' },
  { id: 'structure', percentage: 22, label: 'Structure (columns, beams, slabs)', labelMy: 'Struktur (tiang, rasuk, papak)' },
  { id: 'walls', percentage: 17, label: 'Walls, plaster & paint', labelMy: 'Dinding, plaster & cat' },
  { id: 'flooring', percentage: 14, label: 'Flooring & tiling', labelMy: 'Lantai & jubin' },
  { id: 'roofing', percentage: 16, label: 'Roof & ceiling', labelMy: 'Bumbung & siling' },
  { id: 'me', percentage: 12, label: 'Electrical & plumbing', labelMy: 'Elektrik & paip' },
  { id: 'others', percentage: 7, label: 'Doors, windows & others', labelMy: 'Pintu, tingkap & lain-lain' },
];

const calculateCosts = ({ builtUpArea, finishingLevel, houseType, floors }) => {
  const rates = COST_RATES[finishingLevel];
  const factor = HOUSE_TYPES[houseType].multiplier * getFloorCostFactor(floors);

  const perSqftMin = rates.min * factor;
  const perSqftMax = rates.max * factor;
  const perSqftAvg = (perSqftMin + perSqftMax) / 2;
  const avg = builtUpArea * perSqftAvg;

  return {
    totalCost: {
      min: Math.round(builtUpArea * perSqftMin),
      avg: Math.round(avg),
      max: Math.round(builtUpArea * perSqftMax),
    },
    perSqft: {
      min: Math.round(perSqftMin),
      avg: Math.round(perSqftAvg),
      max: Math.round(perSqftMax),
    },
    categories: COST_CATEGORIES.map((c) => ({ ...c, amount: Math.round((avg * c.percentage) / 100) })),
  };
};

// ---------------------------------------------------------------------------
// Material list
// ---------------------------------------------------------------------------

const item = (id, value, unit, unitMy, label, labelMy, extra = {}) => ({
  id,
  label,
  labelMy,
  unit,
  unitMy,
  ...quantityRange(value, unit),
  ...extra,
});

const buildMaterialGroups = (parts, params) => {
  const { foundation, structure, walls, flooring, roofing, ceiling, services } = parts;
  const concreteRoof = roofing.type === 'concrete';
  const plasterCeiling = ceiling.type === 'plaster';

  const cement =
    foundation.cement + structure.cement + walls.cement + flooring.screedCement +
    (concreteRoof ? roofing.cement : 0) + (plasterCeiling ? ceiling.cement : 0);
  const sandFine = walls.sand + flooring.screedSand + (plasterCeiling ? ceiling.sand : 0);
  const sandCoarse = foundation.sand + structure.sand + (concreteRoof ? roofing.sand : 0);
  const stone = foundation.stone + structure.stone + (concreteRoof ? roofing.stone : 0);
  const steelKg = foundation.steel + structure.steel + (concreteRoof ? roofing.steel : 0);
  const concreteVolume = foundation.volume + structure.volume + (concreteRoof ? roofing.volume : 0);

  const tons = (v, density) => {
    const t = quantityRange(v * density, 'ton');
    return { note: `≈ ${t.avg} ton`, noteMy: `≈ ${t.avg} tan` };
  };

  const groups = [
    {
      id: 'concrete',
      label: 'Cement & concrete',
      labelMy: 'Simen & konkrit',
      items: [
        item('cement', cement, 'bags (50kg)', 'beg (50kg)', 'Cement', 'Simen', {
          note: 'Concrete, brickwork, plaster & screed',
          noteMy: 'Konkrit, kerja bata, plaster & screed',
        }),
        item('concreteVolume', concreteVolume, 'm³', 'm³', 'Structural concrete volume', 'Isipadu konkrit struktur', {
          note: 'Foundation, slabs, columns & beams — for ready-mix orders',
          noteMy: 'Asas, papak, tiang & rasuk — untuk pesanan konkrit siap campur',
        }),
      ],
    },
    {
      id: 'aggregates',
      label: 'Sand & aggregates',
      labelMy: 'Pasir & batu baur',
      items: [
        item('sandFine', sandFine, 'm³', 'm³', 'Fine sand (plaster, mortar)', 'Pasir halus (plaster, mortar)', tons(sandFine, 1.5)),
        item('sandCoarse', sandCoarse, 'm³', 'm³', 'Coarse sand (concrete)', 'Pasir kasar (konkrit)', tons(sandCoarse, 1.6)),
        item('stone', stone, 'm³', 'm³', 'Stone / aggregate', 'Batu / batu baur', tons(stone, 1.5)),
      ],
    },
    {
      id: 'steel',
      label: 'Steel reinforcement',
      labelMy: 'Tetulang keluli',
      items: [
        item('reinforcement', steelKg, 'kg', 'kg', 'Steel bars & BRC mesh', 'Besi tetulang & jaring BRC', {
          note: `≈ ${quantityRange(steelKg / 1000, 'ton').avg} ton`,
          noteMy: `≈ ${quantityRange(steelKg / 1000, 'ton').avg} tan`,
        }),
      ],
    },
    {
      id: 'walls',
      label: 'Walls',
      labelMy: 'Dinding',
      items: [
        walls.isBlock
          ? item('blocks', walls.units, 'pcs', 'biji', 'Concrete blocks', 'Blok konkrit')
          : item('bricks', walls.units, 'pcs', 'biji', 'Clay/cement bricks', 'Bata tanah liat/simen', {
            note: `≈ ${Math.ceil(walls.units / 600)} bundles of 600`,
            noteMy: `≈ ${Math.ceil(walls.units / 600)} berkas (600 biji)`,
          }),
      ],
    },
    {
      id: 'finishes',
      label: 'Tiles & paint',
      labelMy: 'Jubin & cat',
      items: [
        item('floorTiles', flooring.floorTiles, 'sqft', 'kaki persegi', 'Floor tiles', 'Jubin lantai'),
        item('wallTiles', flooring.wallTiles, 'sqft', 'kaki persegi', 'Wall tiles (bathroom, kitchen)', 'Jubin dinding (bilik air, dapur)'),
        item('adhesive', flooring.adhesiveBags, 'bags (25kg)', 'beg (25kg)', 'Tile adhesive', 'Pelekat jubin'),
        item('grout', flooring.grout, 'kg', 'kg', 'Tile grout', 'Grout jubin'),
        item('paint', walls.paint, 'litres', 'liter', 'Emulsion paint (2 coats)', 'Cat emulsi (2 lapisan)'),
        item('primer', walls.primer, 'litres', 'liter', 'Primer / sealer', 'Pendasar / sealer'),
      ],
    },
  ];

  if (roofing.type === 'metal') {
    groups.push({
      id: 'roofing',
      label: 'Roofing',
      labelMy: 'Bumbung',
      items: [
        item('sheets', roofing.sheets, 'sqft', 'kaki persegi', 'Metal deck roofing sheets', 'Kepingan bumbung dek logam'),
        item('screws', roofing.screws, 'pcs', 'biji', 'Roofing screws', 'Skru bumbung'),
      ],
    });
  } else if (roofing.type === 'tiles') {
    groups.push({
      id: 'roofing',
      label: 'Roofing',
      labelMy: 'Bumbung',
      items: [item('roofTiles', roofing.tiles, 'pcs', 'keping', 'Roof tiles', 'Jubin bumbung')],
    });
  }

  if (ceiling.type === 'gypsum') {
    groups.push({
      id: 'ceiling',
      label: 'Ceiling',
      labelMy: 'Siling',
      items: [
        item('boards', ceiling.boards, "boards (4'×8')", "papan (4'×8')", 'Gypsum boards', 'Papan gipsum'),
        item('furring', ceiling.furring, 'pcs', 'batang', 'Metal furring channels', 'Saluran furring logam'),
      ],
    });
  } else if (ceiling.type === 'pvc') {
    groups.push({
      id: 'ceiling',
      label: 'Ceiling',
      labelMy: 'Siling',
      items: [item('panels', ceiling.panels, 'sqft', 'kaki persegi', 'PVC ceiling panels', 'Panel siling PVC')],
    });
  }

  groups.push({
    id: 'services',
    label: 'Electrical & plumbing points',
    labelMy: 'Titik elektrik & paip',
    items: [
      item('lightPoints', services.lightPoints, 'points', 'titik', 'Lighting points', 'Titik lampu'),
      item('socketPoints', services.socketPoints, 'points', 'titik', 'Power sockets', 'Soket kuasa'),
      item('fanPoints', services.fanPoints, 'points', 'titik', 'Ceiling fan points', 'Titik kipas siling'),
      item('waterPoints', services.waterPoints, 'points', 'titik', 'Water points', 'Titik air'),
    ],
  });

  return groups.map((g) => ({ ...g, items: g.items.filter((i) => i.avg > 0) })).filter((g) => g.items.length);
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

const runEstimate = (formData, mode) => {
  const params = deriveCalculationParams(formData);

  const parts = {
    foundation: calculateFoundation(params),
    structure: calculateStructure(params),
    walls: calculateWalls(params),
    flooring: calculateFlooring(params),
    roofing: calculateRoofing(params),
    ceiling: calculateCeiling(params),
    services: calculateServices(params),
  };

  return {
    version: 2,
    mode,
    inputs: {
      ...formData,
      houseType: params.houseType,
      builtUpArea: params.builtUpArea,
      floors: params.floors,
      finishingLevel: params.finishingLevel,
      projectName: (formData.projectName || '').trim(),
    },
    params,
    summary: calculateCosts(params),
    materialGroups: buildMaterialGroups(parts, params),
    generatedAt: new Date().toISOString(),
  };
};

/**
 * Quick estimate: house type, total built-up area, floors and finishing level
 */
export const calculateQuickEstimate = ({ houseType, builtUpArea, floors, finishingLevel, projectName = '' }) => {
  if (!houseType || !builtUpArea || !floors || !finishingLevel) {
    throw new Error('Missing required parameters');
  }
  return runEstimate(
    { ...getDefaultSpecs(houseType, builtUpArea, floors), finishingLevel, projectName },
    'quick'
  );
};

/**
 * Detailed estimate from the full specification form
 */
export const calculateDetailedEstimate = (formData) => runEstimate(formData, 'detailed');

// ---------------------------------------------------------------------------
// Saved data compatibility
// ---------------------------------------------------------------------------

const LEGACY_GROUP_LABELS = {
  concrete: ['Cement & concrete', 'Simen & konkrit'],
  aggregates: ['Sand & aggregates', 'Pasir & batu baur'],
  bricks: ['Walls', 'Dinding'],
  steel: ['Steel reinforcement', 'Tetulang keluli'],
  finishing: ['Tiles & paint', 'Jubin & cat'],
  roofing: ['Roofing', 'Bumbung'],
  ceiling: ['Ceiling', 'Siling'],
};

/**
 * Convert any saved estimate (current or from older app versions) to the current shape.
 * Returns null when the data can't be read.
 */
export const normalizeEstimate = (raw) => {
  if (!raw || typeof raw !== 'object') return null;
  if (raw.version === 2 && raw.summary && Array.isArray(raw.materialGroups)) return raw;

  const inputs = raw.inputs || {};

  // Old detailed ("complete home") results: summary + materials object
  if (raw.summary && raw.summary.totalCost) {
    const { totalCost, perSqft = {}, categories = [] } = raw.summary;
    const materialGroups = Object.entries(raw.materials || {})
      .map(([id, items]) => ({
        id,
        label: LEGACY_GROUP_LABELS[id]?.[0] || id,
        labelMy: LEGACY_GROUP_LABELS[id]?.[1] || id,
        items: Object.entries(items || {})
          .filter(([, v]) => v && typeof v.avg === 'number')
          .map(([itemId, v]) => ({ id: itemId, ...v, tons: undefined, bundles: undefined })),
      }))
      .filter((g) => g.items.length);

    return {
      ...raw,
      version: 2,
      mode: 'detailed',
      legacy: true,
      inputs: { ...inputs, builtUpArea: Number(inputs.builtUpArea) || raw.params?.builtUpArea },
      summary: {
        totalCost,
        perSqft: { ...perSqft, avg: Math.round(((perSqft.min || 0) + (perSqft.max || 0)) / 2) },
        categories,
      },
      materialGroups,
    };
  }

  // Old quick results: costs + breakdown + materials with single quantities
  if (raw.costs && typeof raw.costs.average === 'number') {
    const { costs, breakdown = {} } = raw;
    const categories = Object.entries(breakdown)
      .filter(([, v]) => v && typeof v.amount === 'number')
      .map(([id, v]) => ({ id, label: v.label, labelMy: v.labelMy, percentage: v.percentage, amount: v.amount }));
    const items = Object.entries(raw.materials || {})
      .filter(([, v]) => v && typeof v.quantity === 'number')
      .map(([id, v]) => ({
        id,
        label: v.label,
        labelMy: v.labelMy,
        unit: v.unit,
        unitMy: v.unitMy,
        min: v.quantity,
        avg: v.quantity,
        max: v.quantity,
      }));

    return {
      ...raw,
      version: 2,
      mode: 'quick',
      legacy: true,
      inputs,
      summary: {
        totalCost: { min: costs.minimum, avg: costs.average, max: costs.maximum },
        perSqft: {
          min: costs.perSqftMin,
          max: costs.perSqftMax,
          avg: Math.round(((costs.perSqftMin || 0) + (costs.perSqftMax || 0)) / 2),
        },
        categories,
      },
      materialGroups: items.length ? [{ id: 'materials', label: 'Main materials', labelMy: 'Bahan utama', items }] : [],
    };
  }

  return null;
};
